'use client';

import React, { useCallback, useMemo } from 'react';
import { observer } from '@legendapp/state/react';
import type { CouponActionProps } from './coupon-action.types';
import { useCouponHandler } from '../../hooks/use-coupon-handler';
import { useTranslation } from 'next-i18next';
import { Button, cn, CouponInput, Text, withDialog, type WithDialogProps } from '@kidslabs/mario-nextjs-react-ui-core';
import styles from './coupon-action.module.css';

const CouponActionInner: React.FC<CouponActionProps & WithDialogProps> = observer(
    ({
        appliedCoupons,
        appliedVouchers,
        variant = 'footer',
        containerClassname,
        inputContainerClassname,
        buttonLabel,
        handleNavigateToCouponScreen,
        showDialog,
        hideDialog,
        setDialogOptions,
        onRefresh,
        onSuccess,
        isDisabled: isExternallyDisabled,
        shippingDiscountLabel,
    }) => {
        const { t } = useTranslation('flash-sale');

        const navigateToScreen = useCallback(() => {
            // If onSuccessNavigate is provided (from BottomSheet), use it instead of router.push
            if (onSuccess) {
                onSuccess();
            }
        }, [onSuccess]);

        const {
            couponText,
            isButtonDisabled,
            removeLoading,
            applyLoading,
            handleOnChangeText,
            handleSelectedCouponPress,
            isSelectedCoupon,
            handleApplyCode,
            handleRemoveAppliedCode,
        } = useCouponHandler({
            onSuccess: navigateToScreen,
            appliedCoupons,
            appliedVouchers,
            setDialogOptions,
            showDialog,
            hideDialog,
            onRefresh,
        });

        const isCouponInputDisabled = useMemo(
            () => isExternallyDisabled || !couponText.trim() || removeLoading || applyLoading,
            [isExternallyDisabled, couponText, removeLoading, applyLoading]
        );

        const isCouponApplied = !!appliedCoupons?.[0]?.code;

        const isLoading = removeLoading || applyLoading;

        const handlePressCouponInput = useCallback(() => {
            if (!isCouponInputDisabled) {
                handleApplyCode(couponText);
            }
        }, [isCouponInputDisabled, handleApplyCode, couponText]);

        const handleRemoveCoupon = useCallback(async () => {
            await handleRemoveAppliedCode();
        }, [handleRemoveAppliedCode]);

        const onRightButtonClick = useCallback(async () => {
            if (isCouponApplied) {
                if (couponText.trim()) {
                    await handleApplyCode(couponText);
                    return;
                }
                await handleRemoveCoupon();
                return;
            }

            if (!couponText.trim()) {
                handleNavigateToCouponScreen?.();
            }

            handlePressCouponInput();
        }, [
            handlePressCouponInput,
            handleRemoveCoupon,
            handleApplyCode,
            handleNavigateToCouponScreen,
            isCouponApplied,
            couponText,
        ]);

        const labelButton = useMemo(
            () => (isSelectedCoupon ? t('text_discard_coupon') : t('text_use')),
            [isSelectedCoupon, t]
        );

        const labelClassName = useMemo(() => (isSelectedCoupon ? styles.textWithApplied : null), [isSelectedCoupon]);
        const buttonClassName = useMemo(() => (isSelectedCoupon ? styles.buttonWithApplied : null), [isSelectedCoupon]);

        if (variant === 'header') {
            return (
                <CouponInput
                    className={cn(styles.couponInputHeader, containerClassname)}
                    contentClassName={cn(styles.buttonContainer)}
                    inputClassName={cn(styles.inputContainer, inputContainerClassname)}
                    variant={'detailed'}
                    hasBorderRadius={false}
                    inputPlaceholder={t('text_enter_promo_code')}
                    buttonLabel={isLoading ? t('text_processing_coupon') : (buttonLabel ?? t('text_add_code'))}
                    textValue={couponText}
                    onChangeText={handleOnChangeText}
                    isDisabled={isCouponInputDisabled}
                    onPressCoupon={onRightButtonClick}
                    onKeyDownEnter={onRightButtonClick}
                    hasIconRight
                    couponText={shippingDiscountLabel}
                />
            );
        }

        return (
            <Button
                className={cn(styles.button, buttonClassName)}
                onPress={handleSelectedCouponPress}
                disabled={isExternallyDisabled || isButtonDisabled}
            >
                <Text size="large" className={cn(styles.text, labelClassName)}>
                    {labelButton}
                </Text>
            </Button>
        );
    }
);

const CouponActionContent = withDialog(CouponActionInner);

export const CouponAction = (props: CouponActionProps) => {
    return <CouponActionContent {...props} />;
};
